import { Alert, Button, CircularProgress, Grid, TextField, TextFieldProps } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useAdminStore } from '@tom-ui/admin'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FieldStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { IsoCodeMappingFormDialog } from 'modules/admin/src/pages/IsoCodeMapping/components/IsoCodeMappingForm'
import { ContainerHeight, ContainerType } from 'modules/planning/src/app/api'
import { useState } from 'react'

export type IsoCodeMappingFieldProps = {
  field: FieldStore<string>
  label?: string
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  variant?: TextFieldProps['variant']
  inputGridSizes?: IsoCodeMappingFieldGridSizes
  messageGridSizes?: IsoCodeMappingFieldGridSizes
}

type IsoCodeMappingFieldGridSizes = {
  xs?: number
  sm?: number
  md?: number
}

export const renderIsoCodeMappingField = (props: IsoCodeMappingFieldProps): React.ReactElement => (
  <IsoCodeMappingField {...props} />
)

export const IsoCodeMappingField = observer((props: IsoCodeMappingFieldProps) => {
  const { field, label, fullWidth, disabled, required, variant, inputGridSizes, messageGridSizes } =
    props
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const { isoCodeMappingUIStore, isoCodeMappingStore } = useAdminStore()

  const [isIsoCodeValid, setIsIsoCodeValid] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const containerIsoCodeErrorText = (isoCodeError?: string) => {
    if (isoCodeError) {
      if (isoCodeError === 'required') return t('fieldIsRequired', 'Field is required.')
      if (isoCodeError === 'minLength' || isoCodeError === 'maxLength')
        return t('isoCodeMustHave4Digits', 'ISO Code must have 4 digits.')

      return t(isoCodeError)
    }
  }

  return (
    <Grid container spacing={theme.customSpacing.xs}>
      <Grid
        size={
          inputGridSizes ?? {
            xs: 12,
            sm: 6,
            md: 3,
          }
        }
      >
        <TextField
          {...field.props}
          data-cy='container-iso-code-field'
          type='text'
          label={label}
          fullWidth={fullWidth}
          disabled={disabled}
          required={required}
          variant={variant ?? 'outlined'}
          helperText={containerIsoCodeErrorText(field.error)}
          sx={{ '& input': { textTransform: 'uppercase' } }}
          onChange={async event => {
            const isoCode = event.target.value.toUpperCase()
            event.target.value = isoCode
            field.onChange(event)

            if (isoCode.length === 4) {
              try {
                setIsLoading(true)
                const isValid =
                  !!(await isoCodeMappingStore.getContainerCharacteristicsByIsoCode(isoCode))
                setIsLoading(false)
                setIsIsoCodeValid(isValid)
              } finally {
                setIsLoading(false)
              }
            } else {
              setIsIsoCodeValid(false)
            }
          }}
          slotProps={{
            htmlInput: {
              maxLength: 4,
            },
            input: {
              endAdornment: <>{isLoading && <CircularProgress size={20} />}</>,
            },
          }}
        />
      </Grid>
      <Grid
        size={
          messageGridSizes ?? {
            xs: 12,
            sm: 6,
            md: 9,
          }
        }
      >
        {!isLoading && !isIsoCodeValid && field.value?.length === 4 && (
          <Alert
            severity='warning'
            action={
              <Button
                color='inherit'
                size='small'
                onClick={() =>
                  isoCodeMappingUIStore.toggleFormDialog(true, {
                    isoCode: field.value,
                    height: ContainerHeight.Standard,
                    type: ContainerType.General,
                  })
                }
              >
                {t('create', 'Create')}
              </Button>
            }
          >
            {t('unknownIsoCode', 'Unknown ISO code')}
            <br />
            {t('checkForTyposOrCreateCustomMapping', 'Check for typos or create a custom mapping')}
          </Alert>
        )}

        <IsoCodeMappingFormDialog
          viewStore={isoCodeMappingUIStore}
          onFilter={isoCodeMappingStore.getContainerCharacteristicsByIsoCode}
          onSubmitFn={() => setIsIsoCodeValid(true)}
        />
      </Grid>
    </Grid>
  )
})
